import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { refreshAccessToken } from '../utils/authHelpers';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [parentGroupLabel, setParentGroupLabel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [initialized, setInitialized] = useState(false);

  axios.defaults.baseURL = process.env.REACT_APP_API_BASE_URL;
  axios.defaults.withCredentials = true;

  const logout = useCallback(async () => {
  const authToken = axios.defaults.headers.common['Authorization']?.split(' ')[1] || '';
  try {
    await axios.post('/auth/logout', 
      {}, 
      {
        withCredentials: true,
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}` 
        }
      }
    );
  } catch (error) {
    console.warn('Logout warning:', error?.response?.data?.message || 'Proceeding with client cleanup');
  } finally {
    // Set login status to false
    localStorage.setItem('LoginStatus', 'false');
    
    // Clear user session
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
    navigate('/login', { replace: true });

    // Remove tokens
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
  }
}, [navigate]);

const verifySession = useCallback(async () => {
    if (initialized || user) return { success: true, user };
    
    // Prevent verification if we're already on login page
    if (window.location.pathname === '/login') {
      return {
        success: false,
        error: 'not-authenticated',
        message: 'Not authenticated'
      };
    }

  const MAX_ATTEMPTS = 3;
  let attemptCount = 0;

  const attemptVerify = async () => {
    // Check LoginStatus before each attempt
    if (localStorage.getItem('LoginStatus') === 'false') {
      console.log('Aborting verify attempt - LoginStatus set to false');
      throw new Error('Session expired');
    }

    attemptCount++;
    try {
      const response = await axios.get('/auth/verify', {
        withCredentials: true,
        timeout: 5000
      });
      
      localStorage.setItem('LoginStatus', 'true');
      return { success: true, user: response.data.user };
    } catch (error) {
      if (error.response?.status === 401) {
        // Set LoginStatus to false on first 401 error
        if (attemptCount === 1) {
          localStorage.setItem('LoginStatus', 'false');
        }
      }

      // Don't proceed if we've hit max attempts or LoginStatus is false
      if (attemptCount >= MAX_ATTEMPTS || localStorage.getItem('LoginStatus') === 'false') {
        await logout();
        return {
          success: false,
          error: localStorage.getItem('LoginStatus') === 'false' ? 'session-expired' : 'max-attempts',
          message: localStorage.getItem('LoginStatus') === 'false' 
            ? 'Session expired - please login again' 
            : 'Too many attempts. Please login again'
        };
      }

      if (error.response?.status === 401) {
        try {
          const { accessToken } = await refreshAccessToken();
          axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
          return await attemptVerify();
        } catch (refreshError) {
          if (refreshError.message.includes('No refresh token available')) {
            await logout();
            return {
              success: false,
              error: 'session-expired',
              message: 'Session expired - please login again'
            };
          }
          throw refreshError;
        }
      }
      throw error;
    }
  };

  if (!loading) setLoading(true);
  try {
    const result = await attemptVerify();
    return result;
  } catch (error) {
    return {
      success: false,
      error: 'auth-error',
      message: error.message
    };
  } finally {
    setLoading(false);
  }
}, [logout, initialized, user, loading]);
  
  const initializeAuth = useCallback(async () => {
  if (initialized) return;
  
  try {
    setLoading(true);
    // Initialize LoginStatus if not set
    if (!localStorage.getItem('LoginStatus')) {
      localStorage.setItem('LoginStatus', 'false');
    }
    
    const result = await verifySession();
    
    // Check for success and proper data structure
    if (result?.success && result?.user) {
      console.log("TEST SUCCESS");
      setUser(result.user);
      localStorage.setItem('LoginStatus', 'true');
    } else {
      // Handle case where verifySession didn't return expected data
      console.warn('Session verification returned unexpected data:', result);
      localStorage.setItem('LoginStatus', 'false');
      setUser(null);
    }
  } catch (error) {
    localStorage.setItem('LoginStatus', 'false');
    setUser(null);
    
    if (error.message === 'MAX_ATTEMPTS_REACHED') {
      console.warn('Authentication: Too many failed attempts');
      await logout();
    } else if (error.response?.status === 401) {
      console.log('No active session found');
    } else {
      console.error('Auth initialization error:', error);
    }
  } finally {
    setLoading(false);
    setInitialized(true);
  }
}, [verifySession, logout, initialized]);

  useEffect(() => {
    if (!initialized) {
      initializeAuth();
      setInitialized(true);
    }
  
  const interceptor = axios.interceptors.response.use(
    response => response,
    async (error) => {
      const originalRequest = error.config;
      
      if (error.response?.status === 401 && !originalRequest._retry) {
        try {
          const { accessToken } = await refreshAccessToken();
          axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return axios(originalRequest);
        } catch (refreshError) {
          await logout();
          return Promise.reject(refreshError);
        }
      }
      return Promise.reject(error);
    }
  );
  return () => {
    axios.interceptors.response.eject(interceptor);
    //setInitialized(false); 
  };
}, [initializeAuth, initialized, logout]);

  const login = async (email, password) => {
  try {
    setLoading(true);
    setError(null);

    const response = await axios.post('/auth/login', { email, password }, {
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });
    
    // Set ALL auth states synchronously
    localStorage.setItem('LoginStatus', 'true');
    setUser(response.data.user);
    axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.accessToken}`;
    
    return { success: true };
  } catch (error) {
    localStorage.setItem('LoginStatus', 'false');
    let errorMessage = 'Login failed';
    if (error.response) {
      errorMessage = error.response.data?.error || 
                   (error.response.status === 401 ? 'Invalid credentials' : errorMessage);
    }
    
    // Set login status to false on error
    localStorage.setItem('LoginStatus', 'false');
    setError(errorMessage);
    return { success: false, error: errorMessage };
  } finally {
    setLoading(false);
  }
};

  const updateUser = useCallback((updates) => {
    setUser((prev) => (prev ? { ...prev, ...updates } : prev));
  }, []);

  // Resolve the tenant (parentgroup) label whenever the user changes.
  // The JWT payload carries `parentgroup_fk`, but the user object on the
  // login response may or may not include it — we tolerate both shapes
  // and only fetch when we have an integer FK we haven't resolved yet.
  const parentGroupFk = user?.parentgroup_fk ?? user?.parentGroup ?? null;
  useEffect(() => {
    let cancelled = false;
    if (!Number.isInteger(parentGroupFk)) {
      setParentGroupLabel(null);
      return undefined;
    }
    (async () => {
      try {
        const res = await axios.get(
          `/parentgroups/getParentGroupNameByStatusCode/${parentGroupFk}`,
          { withCredentials: true }
        );
        if (!cancelled) {
          setParentGroupLabel(res.data?.statuslabel || `#${parentGroupFk}`);
        }
      } catch (err) {
        if (!cancelled) {
          setParentGroupLabel(`#${parentGroupFk}`);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [parentGroupFk]);

  return (
    <AuthContext.Provider value={{
      user,
      login,
      logout,
      loading,
      error,
      isAuthenticated: !!user && localStorage.getItem('LoginStatus') === 'true',
      verifySession,
      updateUser,
      parentGroupLabel
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};